import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, OnDestroy, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PaginationModule } from '@frontend/shared';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { AlertModule } from 'ngx-bootstrap/alert';
import { Observable, Subscription } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { LoaderGlobeAnimationDetailComponent, LoadingObject, LoadingService } from '../../loaders';
import { WindowService } from '../../utilities';
import { Notification } from '../notification.model';
import { NotificationService } from '../notification.service';
import { NotificationTargetService } from '../notification-target.service';
import { PaginatedNotifications } from '../paginated-notifications.model';

/** What the page shows, as its query string holds it: `?unread=true&page=2`. */
export interface NotificationsPageState {
  unreadOnly: boolean;
  page: number;
}

/**
 * The reader's notifications, newest first, a page at a time: all of them or
 * the unread only. A reader opens one, marks it read or unread, deletes it, or
 * marks them all read; and finds here the way to their settings, to stop what
 * they no longer want.
 *
 * Its state lives in the URL, so a link or a reload rebuilds the same page.
 * Mount it behind `authGuard`:
 *
 * ```ts
 * { path: 'notifications', canActivate: [authGuard],
 *   loadComponent: () => import('@frontend/common').then((m) => m.NotificationsPageComponent),
 *   data: { label: 'Notifications', translationKey: 'navigation.notifications', labelSource: null } }
 * ```
 *
 * The settings links are named with the account scope's own keys, since they
 * lead to the account's pages.
 */
@Component({
  selector: 'multisite-notifications-page',
  templateUrl: './notifications-page.component.html',
  styleUrls: ['./notifications-page.component.scss'],
  imports: [DatePipe, NgClass, NgTemplateOutlet, RouterModule, TranslocoModule, AlertModule, PaginationModule, LoaderGlobeAnimationDetailComponent],
  providers: [provideTranslocoScope('account')],
})
export class NotificationsPageComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private targetService = inject(NotificationTargetService);
  private loadingService = inject(LoadingService);
  private windowService = inject(WindowService);

  readonly state = toSignal(this.route.queryParamMap.pipe(map((params) => readNotificationsPageState(params.get('unread'), params.get('page')))), {
    requireSync: true,
  });

  notificationsPage = signal<PaginatedNotifications | null>(null);
  failed = signal(false);
  actionFailed = signal(false);
  /** Notifications whose target turned out to be gone when the reader opened them. */
  gone = signal<ReadonlySet<string>>(new Set());

  loadingObject = signal<LoadingObject>({});
  isAnythingLoading = computed(() => this.loadingService.isAnythingLoading(this.loadingObject()));

  private load: Subscription | null = null;

  constructor() {
    effect(() => {
      const state = this.state();
      untracked(() => this.loadPage(state));
    });
  }

  get unreadCount(): number {
    return this.notificationService.unreadCount;
  }

  canOpen(notification: Notification): boolean {
    return this.targetService.canOpen(notification.target);
  }

  chooseUnreadOnly(unreadOnly: boolean): void {
    if (unreadOnly !== this.state().unreadOnly) {
      this.navigate({ unreadOnly, page: 1 });
    }
  }

  changePage(page: number): void {
    this.navigate({ ...this.state(), page });
    this.windowService.goToTop();
  }

  /** Reads the notification and goes where it leads, or says it is gone. */
  open(notification: Notification): void {
    if (!this.targetService.canOpen(notification.target)) {
      return;
    }

    this.setLoadingStatus('notifications:open', true);
    this.targetService
      .open(notification)
      .pipe(finalize(() => this.setLoadingStatus('notifications:open', false)))
      .subscribe({
        next: (went) => {
          if (!went) {
            this.gone.update((gone) => new Set([...gone, notification.id]));
          }
        },
        error: () => this.actionFailed.set(true),
      });
  }

  toggleRead(notification: Notification): void {
    this.act(notification.read ? this.notificationService.markAsUnread(notification.id) : this.notificationService.markAsRead(notification.id));
  }

  remove(notification: Notification): void {
    this.act(this.notificationService.deleteNotification(notification.id));
  }

  markAllAsRead(): void {
    this.act(this.notificationService.markAllAsRead());
  }

  ngOnDestroy(): void {
    this.load?.unsubscribe();
  }

  private setLoadingStatus(key: string, value: boolean): void {
    this.loadingObject.update((current) => this.loadingService.setLoadingStatus(key, value, current));
  }

  private navigate(state: NotificationsPageState): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { unread: state.unreadOnly ? 'true' : null, page: state.page > 1 ? state.page : null },
    });
  }

  /** One change, then the page again: marking and deleting move rows between pages and filters. */
  private act(change: Observable<unknown>): void {
    this.actionFailed.set(false);
    this.setLoadingStatus('notifications:action', true);
    change.pipe(finalize(() => this.setLoadingStatus('notifications:action', false))).subscribe({
      next: () => this.loadPage(this.state()),
      error: () => this.actionFailed.set(true),
    });
  }

  private loadPage(state: NotificationsPageState): void {
    this.load?.unsubscribe();
    this.failed.set(false);

    this.setLoadingStatus('notifications:list', true);
    this.load = this.notificationService
      .getNotifications(state.page, state.unreadOnly)
      .pipe(finalize(() => this.setLoadingStatus('notifications:list', false)))
      .subscribe({
        next: (page) => {
          // The last row of a later page was deleted or read away: step back.
          if (!page.data.length && state.page > 1) {
            this.navigate({ ...state, page: Math.min(state.page - 1, page.meta.last_page || 1) });
            return;
          }
          this.notificationsPage.set(page);
        },
        error: () => {
          this.notificationsPage.set(null);
          this.failed.set(true);
        },
      });
  }
}

export function readNotificationsPageState(unread: string | null, page: string | null): NotificationsPageState {
  return {
    unreadOnly: unread === 'true',
    page: Math.max(1, parseInt(page ?? '1', 10) || 1),
  };
}
